<template>
  <div class="issuance-company">
    <!-- The whole row is a click target for convenience; the chevron is the real,
         keyboard-accessible toggle (native <button> → Enter/Space/focus for free).
         @click.stop on every interactive child keeps the row-click from double-firing. -->
    <div class="issuance-company__header" @click="$emit('toggle')">
      <button
        type="button"
        class="issuance-company__toggle"
        :aria-expanded="expanded"
        :aria-label="`${expanded ? 'Collapse' : 'Expand'} ${company.name ?? 'company'}`"
        @click.stop="$emit('toggle')"
      >
        <font-awesome-icon
          :icon="
            expanded ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'
          "
          class="issuance-company__chevron"
        />
      </button>
      <span class="issuance-company__country">
        <span class="issuance-company__flag">{{ flag }}</span>
        <span class="text-secondary fs-9">{{ countryLabel }}</span>
        <span v-if="registryLabel" class="issuance-company__registry">
          · {{ registryLabel }}
        </span>
      </span>
      <span class="issuance-company__name">
        {{ company.name ?? "—" }}
        <font-awesome-icon
          v-if="company.referrer_company_name"
          icon="fa-solid fa-briefcase"
          class="issuance-company__referrer-icon"
          :title="`Referrer: ${company.referrer_company_name}`"
        />
        <button
          v-if="company.name"
          type="button"
          class="issuance-company__copy"
          title="Copy company name"
          aria-label="Copy company name"
          @click.stop="copyName"
        >
          <font-awesome-icon icon="fa-solid fa-copy" />
        </button>
        <button
          type="button"
          class="issuance-company__copy"
          :disabled="openingDashboard"
          :title="OPEN_DASHBOARD_TITLE"
          :aria-label="OPEN_DASHBOARD_TITLE"
          @click.stop="openCompanyDashboard"
        >
          <font-awesome-icon icon="fa-solid fa-arrow-up-right-from-square" />
        </button>
      </span>
      <span class="issuance-company__metric tabular-nums">
        {{ formatMwh(expectedMwh) }} MWh
      </span>
      <span class="issuance-company__metric tabular-nums">
        {{ formatMwh(issuedMwh) }} MWh
      </span>
      <span class="issuance-company__metric tabular-nums">
        <font-awesome-icon
          v-if="missingCount > 0"
          icon="fa-solid fa-triangle-exclamation"
          class="issuance-company__missing-icon me-1"
          :title="`${missingCount} device(s) expected to issue but issued 0 MWh`"
        />
        {{ issuedDeviceCount }} / {{ activeDeviceCount }}
        <span
          v-if="smallCount > 0"
          class="issuance-company__small-tag"
          :title="smallTooltip"
        >
          +{{ smallCount }}
        </span>
      </span>
      <span class="issuance-company__progress">
        <StackedProgressBar
          :segments="segments"
          :total="100"
          :aria-label="`Devices issued ${formatPercent(devicesIssuedPercent)} percent`"
        />
        <span class="issuance-company__percent tabular-nums">
          <template v-if="activeDeviceCount === 0">—</template>
          <template v-else>{{ formatPercent(devicesIssuedPercent) }}%</template>
        </span>
      </span>
    </div>
    <div v-if="expanded" class="issuance-company__devices">
      <div
        v-for="device in devices"
        :key="device.id"
        class="issuance-company__device"
        :class="{
          'issuance-company__device--muted': deviceTier(device) === 'muted',
          'issuance-company__device--excluded': deviceTier(device) === 'small',
        }"
      >
        <span class="issuance-company__device-name">
          {{ device.name ?? `Device #${device.id}` }}
          <font-awesome-icon
            v-if="device.has_scheduled_transactions"
            icon="fa-solid fa-bomb"
            class="issuance-company__scheduled-icon"
            title="automatic transfers configured in the registry"
          />
          <span
            v-if="isNewDevice(device)"
            class="issuance-company__new-tag"
            :title="`Added ${formatDeviceCreated(device)}`"
          >
            new
          </span>
          <span
            v-if="deviceTier(device) === 'small'"
            class="issuance-company__small-tag"
            title="Excluded from stats (sub-15 kW or sub-2 MWh monthly projection)"
          >
            small
          </span>
        </span>
        <span class="issuance-company__metric tabular-nums">
          {{ formatMwh(device.expected_mwh) }} MWh
        </span>
        <span class="issuance-company__metric tabular-nums">
          {{ formatMwh(device.issued_mwh) }} MWh
        </span>
        <span class="issuance-company__missing">
          <button
            type="button"
            class="issuance-company__mute-btn"
            :class="{
              'issuance-company__mute-btn--active': isActiveMute(device),
              'issuance-company__mute-btn--missing':
                !isActiveMute(device) && isDeviceMissing(device),
            }"
            :title="muteTitle(device)"
            :aria-label="muteTitle(device)"
            @click.stop="openMuteModal(device)"
          >
            <font-awesome-icon
              :icon="
                isActiveMute(device)
                  ? 'fa-solid fa-bell-slash'
                  : 'fa-solid fa-bell'
              "
            />
          </button>
        </span>
        <span class="issuance-company__progress">
          <StackedProgressBar
            :segments="deviceSegments(device)"
            :total="100"
            :aria-label="`Issued ${formatPercent(computeIssuedPercent(device.expected_mwh, device.issued_mwh))} percent`"
          />
          <span class="issuance-company__percent tabular-nums">
            <template v-if="isEmptyDevice(device)">—</template>
            <template v-else>
              {{
                formatPercent(
                  computeIssuedPercent(device.expected_mwh, device.issued_mwh)
                )
              }}%
            </template>
          </span>
        </span>
      </div>
    </div>
  </div>

  <MuteDeviceModal
    v-if="muteModalDevice !== null"
    :is-visible="muteModalDevice !== null"
    :device="muteModalDevice"
    @close="muteModalDevice = null"
    @mute-changed="
      (m) => {
        if (muteModalDevice) muteModalDevice.mute = m;
      }
    "
  />
</template>

<script setup lang="ts">
import {
  companyExpectedMwh,
  companyIssuedMwh,
  computeDevicesIssuedPercent,
  computeIssuedPercent,
  countIssuedDevices,
  countMissingDevices,
  deviceTier,
  isActiveMute,
  isDeviceMissing,
  isEmptyDevice,
  formatMwh,
  formatPercent,
  pickVariant,
  registryShortLabel,
  scoredDevices,
  smallDevices,
  sortedDevices,
} from "@/admin/issuance/issuanceMetrics";
import MuteDeviceModal from "@/admin/issuance/components/MuteDeviceModal.vue";
import { manageCompany } from "@/admin/manageAccount";
import StackedProgressBar from "@/components/common/StackedProgressBar.vue";
import type { StackedProgressBarSegment } from "@/components/common/stackedProgressBarTypes";
import type {
  IssuanceDashboardCompany,
  IssuanceDashboardDevice,
} from "@/types";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { getFlagEmoji, translateCountryCode } from "@/utils/countries";
import { copyToClipboard } from "@/utils/copyUtils";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

const props = defineProps<{
  company: IssuanceDashboardCompany;
  expanded: boolean;
}>();

defineEmits<{
  toggle: [];
}>();

const muteModalDevice = ref<IssuanceDashboardDevice | null>(null);

const muteTitle = (device: IssuanceDashboardDevice): string => {
  if (!isActiveMute(device) || !device.mute) return "Mute device";
  const fmt = (iso: string) => iso.slice(0, 10);
  const reason = device.mute.muted_reason
    ? `: ${device.mute.muted_reason}`
    : "";
  return `Muted ${fmt(device.mute.muted_at)} – ${fmt(device.mute.muted_until)} by ${device.mute.muted_by}${reason}`;
};

const openMuteModal = (device: IssuanceDashboardDevice): void => {
  muteModalDevice.value = device;
};

const { t, te } = useI18n();
const { globalSuccessHandler, globalErrorHandler } = useGlobalHandlers();

const copyName = (): void => {
  if (!props.company.name) return;
  void copyToClipboard(
    props.company.name,
    `Copied "${props.company.name}"`,
    globalSuccessHandler,
    globalErrorHandler
  );
};

const router = useRouter();
const openingDashboard = ref(false);

const OPEN_DASHBOARD_TITLE =
  "Manage this company's owner and open its dashboard in a new tab (the whole session switches to that account)";

// The dashboard resolves the company out of the logged-in account's own companies, so the admin has
// to be impersonating an owner of it before the new tab loads.
const openCompanyDashboard = async (): Promise<void> => {
  // The tab has to be claimed inside the click handler: awaiting the account switch first ends the
  // user activation, and a browser that then blocks the popup would leave the session impersonating
  // with nothing to show for it. `noopener` is left off because it makes window.open return null,
  // and the target is our own origin anyway.
  const dashboardWindow = window.open("", "_blank");
  if (!dashboardWindow) {
    globalErrorHandler(
      new Error("Unable to open dashboard tab — allow popups for this site")
    );
    return;
  }

  openingDashboard.value = true;
  try {
    await manageCompany(props.company.id);
    const { href } = router.resolve({
      name: "Dashboard",
      query: { company_id: String(props.company.id), consolidated: "false" },
    });
    dashboardWindow.location.assign(
      new URL(href, window.location.origin).toString()
    );
  } catch (error) {
    dashboardWindow.close();
    globalErrorHandler(error);
  } finally {
    openingDashboard.value = false;
  }
};

// Devices added within this window get a "new" pill so operators can spot fresh onboardings.
const NEW_DEVICE_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

const isNewDevice = (device: IssuanceDashboardDevice): boolean => {
  const created = new Date(device.created_at).getTime();
  if (Number.isNaN(created)) return false;
  return Date.now() - created < NEW_DEVICE_WINDOW_MS;
};

const formatDeviceCreated = (device: IssuanceDashboardDevice): string =>
  device.created_at.slice(0, 10);

const devicesIssuedPercent = computed(() =>
  computeDevicesIssuedPercent(props.company.devices)
);

const scoredCompanyDevices = computed(() =>
  scoredDevices(props.company.devices)
);

const issuedDeviceCount = computed(() =>
  countIssuedDevices(scoredCompanyDevices.value)
);

const activeDeviceCount = computed(() => scoredCompanyDevices.value.length);

const missingCount = computed(() =>
  countMissingDevices(scoredCompanyDevices.value)
);

// Small devices are shown in the device list and in MWh totals, but kept out of the scored
// count/progress; surface a "+N small" chip so the header reconciles with the expanded list.
const smallCompanyDevices = computed(() => smallDevices(props.company.devices));
const smallCount = computed(() => smallCompanyDevices.value.length);
const smallIssuedCount = computed(() =>
  countIssuedDevices(smallCompanyDevices.value)
);
const smallTooltip = computed(() => {
  const n = smallCount.value;
  const issued = smallIssuedCount.value;
  return `${n} small device${n === 1 ? "" : "s"} (sub-15 kW / <2 MWh): ${issued} issued, ${n - issued} missing — counted in MWh but not in issued% or alerts`;
});

const expectedMwh = computed(() => companyExpectedMwh(props.company));
const issuedMwh = computed(() => companyIssuedMwh(props.company));

const flag = computed(() =>
  props.company.country ? getFlagEmoji(props.company.country) : ""
);

const countryLabel = computed(() =>
  props.company.country
    ? translateCountryCode(props.company.country, t, te)
    : "—"
);

const registryLabel = computed(() =>
  props.company.registry ? registryShortLabel(props.company.registry) : ""
);

const segments = computed<StackedProgressBarSegment[]>(() => {
  // A company-registry with only small/ignored devices has no scored devices — show a flat
  // neutral bar instead of a danger-red 0% that would misread as a total miss.
  if (activeDeviceCount.value === 0) {
    return [{ key: "issued", value: 0, variant: "neutral" }];
  }
  const pct = devicesIssuedPercent.value;
  return [
    {
      key: "issued",
      value: Math.min(100, pct),
      variant: pickVariant(pct),
      title: `${formatPercent(pct)}%`,
    },
  ];
});

const deviceSegments = (
  device: IssuanceDashboardDevice
): StackedProgressBarSegment[] => {
  // Empty rows shouldn't paint danger-red at 0%; show a flat neutral bar instead.
  if (isEmptyDevice(device)) {
    return [{ key: "issued", value: 0, variant: "neutral" }];
  }
  const pct = computeIssuedPercent(device.expected_mwh, device.issued_mwh);
  const filled = Math.min(100, pct);
  const variant = pickVariant(pct);
  return [
    { key: "issued", value: filled, variant, title: `${formatPercent(pct)}%` },
  ];
};

const devices = computed(() => sortedDevices(props.company.devices));
</script>

<style lang="scss" scoped>
.issuance-company {
  border: 1px solid var(--phoenix-border-color);
  border-radius: 0.375rem;
  background: #fff;
}

.issuance-company__header {
  display: grid;
  grid-template-columns: 1.25rem 9rem 1fr 6rem 6rem 9rem 24rem;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 0.75rem 1rem;
  cursor: pointer;
}

.issuance-company__header:hover {
  background: var(--phoenix-gray-100);
}

.issuance-company__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
}

.issuance-company__chevron {
  color: var(--phoenix-gray-600);
  font-size: 0.75rem;
}

.issuance-company__country {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.issuance-company__registry {
  font-size: 0.75rem;
  color: var(--phoenix-gray-600);
}

.issuance-company__flag {
  font-size: 1.1rem;
  line-height: 1;
}

.issuance-company__name {
  font-weight: 600;
}

.issuance-company__copy {
  margin-left: 0.25rem;
  padding: 0.375rem 0.5rem;
  color: var(--phoenix-gray-400);
  font-size: 0.75rem;
  font-weight: 400;
  text-decoration: none;
  cursor: pointer;
  border: none;
  background: none;
  border-radius: 0.25rem;
  opacity: 0;
  transition:
    opacity 0.1s ease,
    color 0.1s ease;
}

.issuance-company__header:hover .issuance-company__copy {
  opacity: 1;
}

.issuance-company__copy:hover {
  color: var(--phoenix-gray-700);
  background: var(--phoenix-gray-200);
}

.issuance-company__copy:disabled {
  color: var(--phoenix-gray-300);
  background: none;
  cursor: default;
}

.issuance-company__copy:focus-visible {
  opacity: 1;
  outline: 2px solid var(--phoenix-primary, #2563eb);
  outline-offset: 1px;
}

.issuance-company__metric {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--phoenix-gray-700);
}

.issuance-company__progress {
  display: grid;
  grid-template-columns: 1fr 3.5rem;
  align-items: center;
  gap: 0.75rem;
}

.issuance-company__percent {
  font-weight: 600;
  text-align: right;
}

.issuance-company__devices {
  border-top: 1px solid var(--phoenix-border-color);
  padding: 0.25rem 1rem 0.5rem;
}

.issuance-company__device {
  display: grid;
  grid-template-columns: 1.25rem 9rem 1fr 6rem 6rem 9rem 24rem;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  color: var(--phoenix-gray-700);
  border-bottom: 1px solid var(--phoenix-gray-100);
}

.issuance-company__device--muted {
  opacity: 0.5;
}

.issuance-company__device--excluded {
  opacity: 0.55;
  font-style: italic;
}

.issuance-company__small-tag {
  display: inline-block;
  margin-left: 0.375rem;
  padding: 0 0.375rem;
  font-size: 0.65rem;
  font-weight: 600;
  font-style: normal;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: var(--phoenix-gray-200);
  color: var(--phoenix-gray-700);
  border-radius: 0.25rem;
}

.issuance-company__new-tag {
  display: inline-block;
  margin-left: 0.375rem;
  padding: 0 0.375rem;
  font-size: 0.65rem;
  font-weight: 600;
  font-style: normal;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: var(--phoenix-info-bg-subtle);
  color: var(--phoenix-info-text-emphasis);
  border-radius: 0.25rem;
  cursor: default;
}

.issuance-company__referrer-icon {
  margin-left: 0.375rem;
  color: var(--phoenix-gray-500);
  font-size: 0.75rem;
  font-weight: 400;
  cursor: default;
}

.issuance-company__scheduled-icon {
  margin-left: 0.375rem;
  color: var(--phoenix-warning);
  font-size: 0.75rem;
  font-weight: 400;
  cursor: default;
}

.issuance-company__device:last-child {
  border-bottom: none;
}

.issuance-company__device-name {
  grid-column: 3 / 4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.issuance-company__missing {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.issuance-company__missing-icon {
  color: var(--phoenix-danger);
}

.tabular-nums {
  font-variant-numeric: tabular-nums;
}

.issuance-company__mute-btn {
  background: none;
  border: none;
  padding: 0.375rem 0.5rem;
  cursor: pointer;
  color: var(--phoenix-gray-300);
  font-size: 0.75rem;
  line-height: 1;
  border-radius: 0.25rem;
}

.issuance-company__mute-btn:hover {
  color: var(--phoenix-gray-600);
  background: var(--phoenix-gray-200);
}

.issuance-company__mute-btn--active {
  color: var(--phoenix-gray-500);
}

.issuance-company__mute-btn--missing {
  color: #c97c7c;
}
</style>
